import React from "react";
import { fetchEntitiesAll, fetchEntityActions } from "~/services";
import { TurnrCard, TurnrCardContent, TurnrCardHeader } from "~/components/card/TurnrCard";
import { serviceIsLoading, serviceIsLoadingOrNotFetched, useServiceHook } from "rylak-common";
import { TurnrGrid } from "~/components/TurnrGrid";
import EntityActionCreateButton from "~/components/entityAction/EntityActionCreateButton.jsx";
import EntityActionsDotsMenu from "~/components/entityAction/EntityActionsDotsMenu.jsx";
import LineDivider from "rylak-common/src/components/divider/LineDivider.jsx";
import { hasCreateEntityAction } from "~/components/entityAction/utils/entityActionsCore.js";

function EntityRow(props) {
    const { appProps, entity, onSuccess, EntityContentComponent, readOnly, entityContentComponentProps, initialLanguageCode, tightRows } = props;
    return (
        <div>
            <LineDivider />
            <div style={{ height: "100%", overflow: "visible", display: "flex", alignItems: "center ", gap: "5px", justifyContent: "space-between", padding: tightRows ? "8px 16px" : "16px" }}>
                <EntityContentComponent appProps={appProps} entity={entity} {...entityContentComponentProps} />
                {!readOnly && <EntityActionsDotsMenu key={0} appProps={appProps} entity={entity} onSuccess={onSuccess} initialLanguageCode={initialLanguageCode} />}
            </div>
        </div>
    );
}

function EntityListRenderer(props) {
    const { appProps, title, description, entities, onSuccess, EntityContentComponent, entityContentComponentProps, entitiesActionsServiceData, loading, initialLanguageCode, tightRows } = props;

    return (
        <TurnrCard>
            {title && <TurnrCardHeader title={title} />}
            {description && <TurnrCardContent className={"pt0 pb2"}>{description}</TurnrCardContent>}

            <TurnrGrid container spacing={1}>
                <TurnrGrid item xs={12}>
                    <div>
                        {entities?.map((entity) => {
                            return (
                                <EntityRow
                                    key={entity.id}
                                    appProps={appProps}
                                    entity={entity}
                                    EntityContentComponent={EntityContentComponent}
                                    entityContentComponentProps={entityContentComponentProps}
                                    onSuccess={onSuccess}
                                    readOnly={false}
                                    initialLanguageCode={initialLanguageCode}
                                    tightRows={tightRows}
                                />
                            );
                        })}
                        {!loading && entitiesActionsServiceData && hasCreateEntityAction(entitiesActionsServiceData?.data) && (
                            <div>
                                <LineDivider />
                                <div style={{ margin: "10px" }}>
                                    <EntityActionCreateButton
                                        appProps={appProps}
                                        onSuccess={onSuccess}
                                        entityActionsServiceData={entitiesActionsServiceData}
                                        initialLanguageCode={initialLanguageCode}
                                    />
                                </div>
                            </div>
                        )}
                        {loading && <TurnrCard loading={true} loadingMinHeight={"270px"} />}
                        {serviceIsLoading(entitiesActionsServiceData) && <TurnrCard loading={true} loadingMinHeight={"270px"} />}
                    </div>
                </TurnrGrid>
            </TurnrGrid>
        </TurnrCard>
    );
}

export default function EntityList(props) {
    const { appProps, entityType, entitiesServiceData, fetchEntitiesQueryParams, onUpdate, Renderer = EntityListRenderer, PreEntityListComponent } = props;

    let { serviceData: entitiesActionsServiceData } = useServiceHook({
        appProps,
        fetchRequested: true,
        fetchData: {
            entityType: entityType,
        },
        fetchFn: fetchEntityActions,
    });

    const isUsingLocalEntitiesServiceData = !entitiesServiceData;

    let { serviceData: entitiesServiceDataLocal, fetchServiceData: fetchEntitiesServiceData } = useServiceHook({
        appProps,
        fetchRequested: isUsingLocalEntitiesServiceData,
        fetchData: {
            entityType: entityType,
            ...fetchEntitiesQueryParams,
        },
        fetchFn: fetchEntitiesAll,
    });
    const actualEntitiesServiceData = isUsingLocalEntitiesServiceData ? entitiesServiceDataLocal : entitiesServiceData;

    const entities = actualEntitiesServiceData?.data;
    const loading = serviceIsLoadingOrNotFetched(actualEntitiesServiceData || entitiesActionsServiceData);

    function onSuccess() {
        if (isUsingLocalEntitiesServiceData) {
            fetchEntitiesServiceData(null, { silentFetch: true });
        }
        onUpdate?.();
    }

    return (
        <>
            {PreEntityListComponent && <PreEntityListComponent entitiesServiceData={actualEntitiesServiceData} />}

            <Renderer {...props} entities={entities} loading={loading} onSuccess={onSuccess} entitiesActionsServiceData={entitiesActionsServiceData} />
        </>
    );
}
